/**
 * Bộ đồ vẽ dùng chung cho 3 mẫu ảnh QR: nạp ảnh, vẽ chữ, vẽ nhãn bo tròn.
 *
 * Tách khỏi từng mẫu để 3 mẫu chỉ còn phần bố cục riêng của nó — sửa cách vẽ
 * chữ một lần là cả 3 mẫu đổi theo, không phải sửa 3 chỗ rồi quên mất một.
 */

import { FONT_STACK } from "./brand";
import { roundedRectPath } from "./qr-matrix";

/**
 * Nạp ảnh về dạng dùng được cho canvas.
 *
 * `crossOrigin` để trống là CỐ Ý: mọi ảnh ở đây đều nằm trong `public/` của
 * chính site, cùng nguồn nên canvas không bị nhiễm bẩn. Đặt `crossOrigin` cho
 * ảnh cùng nguồn lại làm trình duyệt đòi hỏi CORS header mà site không gửi.
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Không tải được ảnh: ${src}`));
    img.src = src;
  });
}

/**
 * Chờ bộ chữ web tải xong trước khi vẽ.
 *
 * Canvas vẽ bằng bộ chữ SẴN CÓ tại đúng khoảnh khắc gọi `fillText`. Chưa tải
 * xong Be Vietnam Pro thì chữ rơi về bộ dự phòng của hệ thống — ảnh gửi khách
 * sai bộ chữ, mà lỗi này chỉ xảy ra ở lần mở đầu tiên nên rất khó lần ra.
 *
 * Trình duyệt cũ không có `document.fonts` thì bỏ qua, vẽ luôn bằng bộ dự phòng.
 */
export async function ensureFontsReady(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load(`700 64px ${FONT_STACK}`),
      document.fonts.load(`600 32px ${FONT_STACK}`),
      document.fonts.load(`400 28px ${FONT_STACK}`),
    ]);
    await document.fonts.ready;
  } catch {
    // Bộ chữ hỏng không được phép chặn việc xuất ảnh — vẽ bằng bộ dự phòng.
  }
}

export type TextOptions = {
  weight?: number;
  size: number;
  color: string;
  align?: CanvasTextAlign;
  /** Giãn chữ, tính bằng pixel — dùng cho nhãn viết hoa nhỏ. */
  letterSpacing?: number;
};

/** Vẽ một dòng chữ. Trả về bề rộng thực đo được, để người gọi xếp tiếp. */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  { weight = 400, size, color, align = "left", letterSpacing = 0 }: TextOptions,
): number {
  ctx.save();
  ctx.font = `${weight} ${size}px ${FONT_STACK}`;
  ctx.fillStyle = color;
  ctx.textAlign = letterSpacing > 0 ? "left" : align;
  ctx.textBaseline = "middle";

  if (letterSpacing <= 0) {
    ctx.fillText(text, x, y);
    const width = ctx.measureText(text).width;
    ctx.restore();
    return width;
  }

  // Giãn chữ: canvas không có thuộc tính này ở mọi trình duyệt app hỗ trợ, nên
  // vẽ từng ký tự. Phải tự canh lề vì đã ép `textAlign = "left"` ở trên.
  const chars = [...text];
  const total =
    chars.reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) +
    letterSpacing * Math.max(0, chars.length - 1);
  let cursor = align === "center" ? x - total / 2 : align === "right" ? x - total : x;
  for (const ch of chars) {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + letterSpacing;
  }
  ctx.restore();
  return total;
}

/**
 * Vẽ chữ tự xuống dòng trong bề ngang cho trước.
 *
 * @returns tung độ đáy khối chữ, để người gọi vẽ tiếp bên dưới.
 */
export function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  options: TextOptions,
): number {
  ctx.save();
  ctx.font = `${options.weight ?? 400} ${options.size}px ${FONT_STACK}`;
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  ctx.restore();

  lines.forEach((l, i) => drawText(ctx, l, x, y + i * lineHeight, options));
  return y + (lines.length - 1) * lineHeight;
}

/**
 * Đo bề ngang một dòng chữ TRƯỚC khi vẽ.
 *
 * Có hàm này thì nền nhãn mới ôm đúng chữ. Đoán bằng con số cứng là sai ngay
 * khi chữ đổi (dài hơn, cỡ khác, bộ chữ dự phòng rộng hơn) — và cái sai đó
 * hiện ra thành mấy ký tự cuối rơi ra ngoài nền màu.
 */
export function measureTextWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  { weight = 400, size, letterSpacing = 0 }: Omit<TextOptions, "color" | "align">,
): number {
  ctx.save();
  ctx.font = `${weight} ${size}px ${FONT_STACK}`;
  const chars = [...text];
  const width =
    chars.reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) +
    letterSpacing * Math.max(0, chars.length - 1);
  ctx.restore();
  return width;
}

export type PillOptions = {
  x: number;
  y: number;
  /** Bỏ trống = ôm sát chữ. */
  width?: number;
  height: number;
  radius?: number;
  fill: string;
  /** Vắng mặt = không kẻ viền. */
  stroke?: string;
};

/** Vẽ nhãn nền bo tròn (dùng cho "MÃ ĐƯỢC XÁC THỰC", ô số tiền…). */
export function drawPill(
  ctx: CanvasRenderingContext2D,
  { x, y, width, height, radius, fill, stroke }: PillOptions,
): void {
  const w = width ?? height;
  roundedRectPath(ctx, x, y, w, height, radius ?? height / 2);
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = stroke;
    ctx.stroke();
  }
}

/**
 * Vẽ hình khiên đặc (biểu tượng an toàn) — 3 mẫu đều dùng.
 *
 * Vẽ tay bằng path thay vì nhúng ảnh: chỉ vài đường bezier, mà nhúng ảnh thì
 * thêm một lượt tải mạng có thể hỏng đúng lúc xuất ảnh.
 */
export function drawShield(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  fill: string,
  checkColor: string,
): void {
  const half = size / 2;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx, cy - half);
  ctx.lineTo(cx + half * 0.8, cy - half * 0.62);
  ctx.lineTo(cx + half * 0.8, cy + half * 0.1);
  ctx.quadraticCurveTo(cx + half * 0.8, cy + half * 0.72, cx, cy + half);
  ctx.quadraticCurveTo(cx - half * 0.8, cy + half * 0.72, cx - half * 0.8, cy + half * 0.1);
  ctx.lineTo(cx - half * 0.8, cy - half * 0.62);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(cx - half * 0.32, cy - half * 0.02);
  ctx.lineTo(cx - half * 0.08, cy + half * 0.26);
  ctx.lineTo(cx + half * 0.36, cy - half * 0.3);
  ctx.lineWidth = Math.max(2, size * 0.11);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = checkColor;
  ctx.stroke();
  ctx.restore();
}

/**
 * Vẽ ổ khoá đóng — biểu tượng "nội dung không được sửa".
 *
 * Vẽ tay chứ KHÔNG dùng emoji 🔒: canvas lấy emoji từ bộ chữ của máy, máy
 * thiếu bộ emoji thì ra ô vuông đậu phụ ngay giữa ảnh gửi khách.
 */
export function drawLock(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  color: string,
): void {
  const bodyW = size * 0.78;
  const bodyH = size * 0.6;
  const bodyY = cy - bodyH / 2 + size * 0.12;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, size * 0.12);
  ctx.lineCap = "round";

  // Quai khoá.
  ctx.beginPath();
  ctx.arc(cx, bodyY, bodyW * 0.32, Math.PI, 0);
  ctx.stroke();

  // Thân khoá.
  ctx.fillStyle = color;
  roundedRectPath(ctx, cx - bodyW / 2, bodyY, bodyW, bodyH, size * 0.12);
  ctx.fill();
  ctx.restore();
}

/** Định dạng tiền cho ảnh: "1.000 đ". Không phụ thuộc locale của máy khách. */
export function formatPosterAmount(amount: number): string {
  const rounded = Math.max(0, Math.round(amount));
  return `${rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} đ`;
}
